import Button from 'tio-ui/components/button';
import { Listbox } from 'tio-ui/components/listbox';
import Popover from 'tio-ui/components/popover';
import { Section } from 'tio-ui/components/layout';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import { PhDotsThreeVertical, PhArrowRight, PhPlus } from 'ember-phosphor-icons';
import { or } from 'tio-ui/utilities';
// Type definition for expense row objects
type ExpenseRow = {
  label: string;
  subtitle?: string;
  amount: string;
  isTotal: boolean;
  type: 'course' | 'scholarship' | 'course-cash' | 'total';
  courseIndex?: number;
};

interface ExpensesSectionSignature {
  Args: {
    model: TasApplicationModel;
    /** Prevents opening admin-only expense editing actions. */
    isReadOnly: boolean;
    /**
     * Section heading + row naming — owned by the parent page because
     * ApprovalFooter and the courses-drawer tab labels also use it
     */
    expenseType: string;
    /** Opens the page-level courses drawer at the given course index */
    onCourseOpen: (courseIndex: number) => void;
    /** Opens the page-level scholarship drawer */
    onScholarshipOpen: () => void;
  };
}

/**
 * Expenses summary section of the approval page: one row per course plus
 * scholarship / course-cash / total rows, and the ⋮ menu for adding a
 * scholarship. Row clicks and the menu delegate to the page-level drawers
 * via onCourseOpen / onScholarshipOpen.
 */
export default class ExpensesSection extends Component<ExpensesSectionSignature> {
  // Expenses popover state
  @tracked isExpensesPopoverOpen = false;

  openExpensesPopover = () => {
    this.isExpensesPopoverOpen = true;
  };

  closeExpensesPopover = () => {
    this.isExpensesPopoverOpen = false;
  };

  handleExpensesMenu = (key: string) => {
    if (this.args.isReadOnly) return;

    // Close popover first
    this.isExpensesPopoverOpen = false;

    // Add delay to allow popover to close before opening drawer
    setTimeout(() => {
      if (key === 'addScholarship') {
        this.args.onScholarshipOpen();
      }
    }, 250);
  };

  handleExpenseRowClick = (row: ExpenseRow) => {
    if (this.args.isReadOnly && row.type === 'scholarship') return;

    if (row.type === 'course' && row.courseIndex !== undefined) {
      this.args.onCourseOpen(row.courseIndex);
    } else if (row.type === 'scholarship') {
      this.args.onScholarshipOpen();
    }
    // course-cash and total rows are not clickable
  };

  get requestedAmount() {
    if (this.args.model.isCreditBased) {
      const credits = this.args.model.totalCredits || 0;
      return `${credits} credits`;
    }
    const amount = this.args.model.requestedTotal || 0;
    return amount ? `$${(amount / 100).toFixed(2)}` : '$0.00';
  }

  get expenseRows() {
    const courses = this.args.model.activeCourses || [];

    // Get course expenses
    const isCreditBased = this.args.model.isCreditBased;
    const courseRows = courses.map((course, index) => {
      const courseName = course.fields?.COURSE_NAME?.values?.[0] || `Course ${index + 1}`;
      const courseNumber = course.courseNumber || '';
      let amount: string;
      if (isCreditBased) {
        const credits = course.courseCredit || 0;
        amount = `${credits} credits`;
      } else {
        const courseCost = course.courseExpenses;
        amount = courseCost ? `$${(courseCost / 100).toFixed(2)}` : '$0.00';
      }

      return {
        label: courseName,
        subtitle: courseNumber ? String(courseNumber) : undefined,
        amount: amount,
        isTotal: false,
        type: 'course' as const,
        courseIndex: index,
      };
    });

    const rows: ExpenseRow[] = [...courseRows];

    // Add scholarships if present
    const scholarshipsTotal = this.args.model.scholarshipsTotal || 0;
    if (scholarshipsTotal > 0) {
      rows.push({
        label: 'Scholarships',
        amount: `-$${(scholarshipsTotal / 100).toFixed(2)}`,
        isTotal: false,
        type: 'scholarship' as const,
      });
    }

    // Add course cash total if using course cash payment method
    const isCourseCashPayment =
      this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    const courseCashTotal = Number(
      this.args.model.requestedCourseCashSubtotalLessScholarships || 0
    );
    if (isCourseCashPayment && courseCashTotal > 0) {
      rows.push({
        label: 'Course Cash Total',
        amount: `$${(courseCashTotal / 100).toFixed(2)}`,
        isTotal: false,
        type: 'course-cash' as const,
      });
    }

    // Add total requested (label changes based on whether course cash is shown)
    const totalLabel =
      isCourseCashPayment && courseCashTotal > 0
        ? 'Total Reimbursement Requested'
        : 'Total Requested';
    rows.push({
      label: totalLabel,
      amount: this.requestedAmount,
      isTotal: true,
      type: 'total' as const,
    });

    return rows;
  }

  <template>
    <Section>
      <:header>
        <div
          class="tio-row-between px-4 py-2.5 tio-bg-sunken border tio-border-subtle rounded-[10px] max-w-3xl"
        >
          <h3 class="tio-text-base-semibold tio-fg-primary">{{@expenseType}}</h3>
          {{#unless @isReadOnly}}
            <Popover
              @isOpen={{this.isExpensesPopoverOpen}}
              @onOpen={{this.openExpensesPopover}}
              @onClose={{this.closeExpensesPopover}}
              @placement="top-end"
            >
              <:trigger as |pop|>
                <Button
                  @appearance="minimal"
                  @size="sm"
                  @ariaLabel="Open expenses menu"
                  {{pop.register}}
                  {{on "click" this.openExpensesPopover}}
                  data-test-expenses-menu-trigger
                >
                  <PhDotsThreeVertical @size="16" class="tio-fg-muted" />
                </Button>
              </:trigger>
              <:body>
                <Listbox @intent="primary" @onAction={{this.handleExpensesMenu}} as |l|>
                  <l.Item @key="addScholarship" data-test-add-scholarship-menu-item>
                    <span class="tio-row-tight">
                      <PhPlus @size="16" />
                      Add Scholarship
                    </span>
                  </l.Item>
                </Listbox>
              </:body>
            </Popover>
          {{/unless}}
        </div>
      </:header>
      <:body>
        <div class="divide-y tio-divide-subtle max-w-3xl">
          {{#each this.expenseRows as |row|}}
            <div class="tio-row-between py-4 px-4">
              <div class="tio-flex-loose">
                <div class="w-96 truncate" title="{{row.label}}">
                  <div class="tio-text-sm-medium tio-fg-secondary">{{row.label}}</div>
                  {{#if row.subtitle}}
                    <div class="text-xs tio-fg-muted">{{row.subtitle}}</div>
                  {{/if}}
                </div>
                <div
                  class="w-56 tio-text-sm-medium
                    {{if row.isTotal 'tio-fg-brand font-semibold' 'tio-fg-secondary'}}"
                >{{row.amount}}</div>
              </div>
              {{! Only show arrow for course and scholarship rows }}
              {{#if
                (or (eq row.type "course") (and (eq row.type "scholarship") (not @isReadOnly)))
              }}
                <Button
                  @appearance="minimal"
                  @size="sm"
                  {{on "click" (fn this.handleExpenseRowClick row)}}
                  class="p-1.5!"
                  data-test-open-expense-detail
                >
                  <PhArrowRight @size="16" class="tio-fg-muted" />
                </Button>
              {{/if}}
            </div>
          {{/each}}
        </div>
      </:body>
    </Section>
  </template>
}
